import React, { useEffect, useState } from "react";
import { Car, Plus, MousePointer2, X, Sparkles, Check } from "lucide-react";

// Micro-démo animée de l'interface Junago (succession animée de "captures").
// 3 étapes qui s'enchaînent en boucle : Type → Catégorie → Véhicule.
const STEPS = [
  { n: 1, title: "Créez votre type de bien", hint: "Un clic sur une suggestion suffit." },
  { n: 2, title: "Ajoutez vos catégories", hint: "Organisez vos biens par famille." },
  { n: 3, title: "Ajoutez vos véhicules", hint: "Rattachez-les à leur catégorie." },
];
const STEP_MS = 3600;

const Stage = ({ step }) => (
  <div className="relative h-[220px] rounded-xl bg-slate-50 border border-slate-200 overflow-hidden" data-testid="fleet-guide-stage">
    {/* fausse barre de fenêtre */}
    <div className="flex items-center gap-1 px-3 h-7 bg-white border-b border-slate-200">
      <span className="h-2 w-2 rounded-full bg-red-300" />
      <span className="h-2 w-2 rounded-full bg-amber-300" />
      <span className="h-2 w-2 rounded-full bg-emerald-300" />
      <span className="ml-2 text-[9px] text-slate-400 font-medium">Flotte · Types & catégories</span>
    </div>

    <div className="relative p-3 h-[calc(100%-1.75rem)]" key={step}>
      {step === 0 && (
        <div className="space-y-2">
          <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Suggestions</div>
          <div className="flex flex-wrap gap-1.5">
            {["Voiture", "Vélo", "Bateau"].map((c, i) => (
              <span key={c} className={`gd-chip inline-flex items-center gap-1 px-2 py-1 rounded-full border border-dashed text-[10px] ${i === 0 ? "gd-target border-violet-400 text-violet-600" : "border-slate-300 text-slate-500"}`}>
                <Plus className="h-2 w-2" /> {c}
              </span>
            ))}
          </div>
          <div className="gd-appear mt-3 flex items-center gap-2 p-2 rounded-lg bg-white border border-slate-200">
            <span className="h-7 w-7 rounded-lg bg-violet-50 border border-violet-100 flex items-center justify-center text-violet-600"><Car className="h-3.5 w-3.5" /></span>
            <span className="flex-1">
              <span className="block text-[11px] font-semibold text-slate-800">Voiture</span>
              <span className="block text-[9px] text-slate-400">0 catégorie</span>
            </span>
            <Check className="h-3.5 w-3.5 text-emerald-500 gd-appear-late" />
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-1.5 rounded-lg bg-white border border-slate-200">
            <span className="h-6 w-6 rounded-md bg-violet-50 flex items-center justify-center text-violet-600"><Car className="h-3 w-3" /></span>
            <span className="text-[11px] font-semibold text-slate-800">Voiture</span>
          </div>
          <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold pt-1">Suggestions de catégories</div>
          <div className="flex flex-wrap gap-1.5">
            {["Compacte", "Berline", "SUV"].map((c, i) => (
              <span key={c} className={`gd-chip inline-flex items-center gap-1 px-2 py-1 rounded-full border border-dashed text-[10px] ${i === 0 ? "gd-target border-violet-400 text-violet-600" : "border-slate-300 text-slate-500"}`}>
                <Plus className="h-2 w-2" /> {c}
              </span>
            ))}
          </div>
          <div className="gd-appear mt-2 flex items-center gap-2 pl-6">
            <span className="h-4 w-px bg-slate-200" />
            <span className="px-2 py-1 rounded-md bg-white border border-slate-200 text-[10px] font-medium text-slate-700">Compacte</span>
            <Check className="h-3 w-3 text-emerald-500 gd-appear-late" />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1 text-[10px] text-slate-400">
            <span className="font-semibold text-violet-600">Voiture</span>
            <span>›</span>
            <span className="font-semibold text-slate-600">Compacte</span>
          </div>
          <div className="gd-appear mt-1 rounded-lg bg-white border border-slate-200 overflow-hidden shadow-sm">
            <div className="h-16 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
              <Car className="h-6 w-6 text-slate-400" />
            </div>
            <div className="p-2">
              <div className="text-[11px] font-semibold text-slate-800">Renault Clio</div>
              <div className="text-[9px] text-slate-400">Voiture · Compacte</div>
            </div>
          </div>
          <div className="gd-appear-late flex items-center gap-1 text-[9px] text-emerald-600 font-medium pt-1">
            <Check className="h-3 w-3" /> Véhicule ajouté à votre flotte
          </div>
        </div>
      )}

      {/* curseur simulé + clic */}
      {step < 2 && (
        <div className="gd-cursor absolute z-10 pointer-events-none">
          <span className="gd-ripple" />
          <MousePointer2 className="h-4 w-4 text-slate-800 fill-white drop-shadow" />
        </div>
      )}
    </div>
  </div>
);

export default function FleetOnboardingGuide({ onHide, embedded = true }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % 3), STEP_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div className={embedded ? "rounded-2xl border border-slate-200 bg-white p-4" : "p-1"} data-testid="fleet-onboarding-guide">
      <style>{`
        @keyframes gdAppear { 0%{opacity:0;transform:translateY(6px) scale(.98)} 100%{opacity:1;transform:none} }
        @keyframes gdPulse { 0%,100%{box-shadow:0 0 0 0 rgba(139,92,246,.0)} 50%{box-shadow:0 0 0 4px rgba(139,92,246,.18)} }
        @keyframes gdRipple { 0%{transform:scale(.4);opacity:.5} 80%,100%{transform:scale(1.9);opacity:0} }
        @keyframes gdCursor { 0%{top:8px;right:8px;left:auto;transform:translate(0,0) scale(1)} 45%{top:58px;left:40px;right:auto;transform:scale(1)} 55%{top:58px;left:40px;right:auto;transform:scale(.8)} 70%,100%{top:58px;left:40px;right:auto;transform:scale(1)} }
        .gd-appear{ animation: gdAppear .5s ease both; animation-delay:1.1s; }
        .gd-appear-late{ animation: gdAppear .5s ease both; animation-delay:1.7s; }
        .gd-target{ animation: gdPulse 1.6s ease-in-out .5s 1 both; }
        .gd-cursor{ animation: gdCursor 2.2s ease-in-out both; }
        .gd-ripple{ position:absolute; top:-2px; left:-2px; height:14px; width:14px; border-radius:9999px; background:rgba(139,92,246,.45); animation: gdRipple 1.6s ease-out .9s 1 both; }
      `}</style>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-violet-500" />
          <span className="text-sm font-semibold text-slate-900">Prise en main</span>
        </div>
        {onHide && (
          <button type="button" onClick={onHide} className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-700" data-testid="fleet-guide-hide">
            <X className="h-3.5 w-3.5" /> Masquer
          </button>
        )}
      </div>

      <Stage step={step} />

      {/* légende étape courante */}
      <div className="mt-3">
        <div className="flex items-center gap-2">
          <span className="h-5 w-5 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{STEPS[step].n}</span>
          <span className="text-xs font-semibold text-slate-800">{STEPS[step].title}</span>
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5 pl-7">{STEPS[step].hint}</div>
      </div>

      {/* barres de progression */}
      <div className="flex gap-1.5 mt-3">
        {STEPS.map((s, i) => (
          <button key={s.n} type="button" onClick={() => setStep(i)} aria-label={`Étape ${s.n}`}
            className={`h-1 flex-1 rounded-full transition-colors ${i === step ? "bg-violet-600" : "bg-slate-200 hover:bg-slate-300"}`}
            data-testid={`fleet-guide-step-${s.n}`} />
        ))}
      </div>
    </div>
  );
}
